"use client";

/**
 * FormDescriptionContent
 * Renders a form's top-level description. New descriptions are authored as rich
 * HTML (see FormDescriptionEditor) and rendered after DOMPurify sanitization.
 * Legacy descriptions were stored as plain text — those are detected and rendered
 * the original way (preserved line breaks + auto-linked URLs) so they don't regress.
 */

import DOMPurify from "dompurify";
import { Fragment, memo, useMemo, type ReactElement, type ReactNode } from "react";

/** Tags allowed in rich descriptions: headings (H1-H3, matching FormDescriptionEditor's toolbar) +
 *  inline marks + lists + links. No tables/blockquote. */
const ALLOWED_TAGS = ["h1", "h2", "h3", "p", "br", "strong", "b", "em", "i", "u", "s", "a", "ul", "ol", "li", "span"];

/** Attributes allowed on description HTML — link attributes only. */
const ALLOWED_ATTR = ["href", "target", "rel"];

/** Matches raw URLs in legacy plain-text descriptions for auto-linking. */
const URL_REGEX = /(https?:\/\/[^\s]+)/g;

/** Detects whether a stored value contains HTML markup (vs. legacy plain text). */
const HTML_TAG_REGEX = /<\/?[a-z][\s\S]*>/i;

// Force rel="noopener noreferrer" on any target="_blank" anchor to prevent reverse-tabnabbing.
// Registered once; additive and security-only, so it is safe across all DOMPurify usages —
// exported so other form rich-text renderers (e.g. ContentBlockPreview) can share it rather
// than each registering their own copy of the same hook.
let hookRegistered = false;
export function ensureSanitizeHook(): void {
  if (hookRegistered) return;
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.nodeName === "A" && node.getAttribute("target") === "_blank") {
      node.setAttribute("rel", "noopener noreferrer");
    }
  });
  hookRegistered = true;
}

/** Splits legacy plain text into text + clickable-link nodes, preserving order. */
function linkifyPlainText(text: string): ReactNode[] {
  return text.split(URL_REGEX).map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="touch-manipulation text-blue-600 [@media(hover:hover)]:hover:underline"
      >
        {part}
      </a>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    )
  );
}

interface FormDescriptionContentProps {
  /** Stored description — rich HTML or legacy plain text. */
  html: string;
  /** Tailwind classes for the wrapper. */
  className?: string;
}

/**
 * Renders a form description, sanitizing rich HTML or falling back to legacy
 * plain-text rendering (preserved newlines + auto-linked URLs).
 * @param html - The stored description (HTML or plain text)
 * @param className - Optional wrapper classes
 * @returns The rendered description, or null when empty
 */
export const FormDescriptionContent = memo(({ html, className }: FormDescriptionContentProps): ReactElement | null => {
  const isHtml = useMemo(() => HTML_TAG_REGEX.test(html ?? ""), [html]);

  const sanitizedHtml = useMemo(() => {
    if (!isHtml) return "";
    ensureSanitizeHook();
    return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR });
  }, [html, isHtml]);

  if (!html?.trim()) return null;

  if (!isHtml) {
    return <div className={`whitespace-pre-line ${className ?? ""}`}>{linkifyPlainText(html)}</div>;
  }

  return (
    <div
      className={`prose prose-sm max-w-none [&_a]:text-blue-600 [&_a]:underline ${className ?? ""}`}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
});
FormDescriptionContent.displayName = "FormDescriptionContent";
